import {Injectable} from '@angular/core';
import {Observable} from 'rxjs/Observable';

import * as Sentry from '@sentry/browser';
import {ActivatedRoute, Router} from '@angular/router';
import {BaseService} from './base.service';
import {TranslateService} from '@ngx-translate/core';
import {ServiceLocator} from '../service-locator';
import {User, UserType} from '../../models/user';
import {ObjectUtils} from '../utils/object.utils';
import {WebUtils} from '../utils/web.utils';
import { apiEndpoints } from '../app.constants';

@Injectable()
export abstract class BackendHttpService<T> {
  static SENTRY_DEFAULT_FINGERPRINT_VALUES = '{{ default }}';

  // This static flag is for avoid checking `localStorage.isSsoUser` multiple times when multiple apis lead to 401 or unknown startup error
  // Because of not using Angular router for ths case, this flag is avoid trigger Angular routing also.
  static IS_NAVIGATING_TO_MMI_LOGOUT = false;

  private baseService: BaseService;
  private activatedRoute: ActivatedRoute;

  constructor( protected router: Router,
               protected translate: TranslateService) {
    this.baseService = ServiceLocator.injector.get(BaseService);
    this.activatedRoute = ServiceLocator.injector.get(ActivatedRoute);
  }

  get(url, options = {}): Observable<T> {
    return this.baseService.get(url, options)
      .map(resp => resp as T)
      .catch(err => this.handleError(err));
  }

  getPDF(url, options = {}): Observable<T> {
    return this.baseService.getPDF(url, options)
      .map(resp => resp as T)
      .catch(err => this.handleError(err));
  }

  getImage(url, options = {}): Observable<Blob> {
    return this.baseService.getImage(url, options)
      .map(resp => resp as Blob)
      .catch(err => this.handleError(err));
  }

  post(url, postData: any, options = {}): Observable<T> {
    return this.baseService.post(url, postData, options)
      .map(resp => resp as T)
      .catch(err => this.handleError(err));
  }

  postRaw(url, postData: any, options = {}): Observable<T> {
    return this.baseService.postRaw(url, postData, options)
      .map(resp => resp as T)
      .catch(err => this.handleError(err));
  }

  put(url, postData: any, options = {}): Observable<T> {
    return this.baseService.put(url, postData, options)
      .map(resp => resp as T)
      .catch(err => this.handleError(err));
  }

  delete(url, options = {}): Observable<T> {
    return this.baseService.delete(url, options)
      .map(resp => resp as T)
      .catch(err => this.handleError(err));
  }

  private handleError(error: Response | any) {
    console.warn(error);
    // don't report 401 error or unknown startup api failures to Sentry
    if (error.status === 401 || this.isUnknownAppStartupApiError(error)) {
      // No need to check or clear localStorage while it navigating to logout page
      if (!BackendHttpService.IS_NAVIGATING_TO_MMI_LOGOUT) {
        const currentUser = ObjectUtils.parseJSON(localStorage.getItem('user')) as User;
        const targetUrl = WebUtils.getTargetUrl();
        const isSsoUser = localStorage.getItem('isSsoUser') === 'true';
        localStorage.removeItem('isSsoUser');
        localStorage.removeItem('user');

        if (currentUser && currentUser.type === UserType.INFLUENCER) {
          // Influencer app
          this.router.navigate(['/influencer/login'], { queryParams: { target_url: targetUrl } });
        } else {
          // Advertiser app
          if (isSsoUser) {
            BackendHttpService.IS_NAVIGATING_TO_MMI_LOGOUT = true;
            WebUtils.navigateToMmiLogoutPage();
          } else {
            this.router.navigate(['/login'], { queryParams: { target_url: targetUrl } });
          }
        }
      }

      return Observable.throw(error);
    }

    // don't report error with status from 400 and above to Sentry
    if (error && error.status >= 400 && error.status < 500) {
      return Observable.throw(error);
    }

    let reported_error;
    let errorPath;

    if (error && error.error instanceof Error) {
      reported_error = error.error;
    } else if (error && error.error && error.error.message) {
      reported_error = new Error(error.error.message);
      errorPath = error.url ? this.getErrorPath(error.url, error.error.message) : null;
    } else if (error && !!error.message) {
      reported_error = new Error(error.message);
      errorPath = error.url ? this.getErrorPath(error.url, error.message) : null;
    } else {
      reported_error = new Error('Unknow HTTP error');
    }

    if (errorPath) {
      Sentry.withScope((scope) => {
        scope.setFingerprint([BackendHttpService.SENTRY_DEFAULT_FINGERPRINT_VALUES, errorPath]);
        Sentry.captureException(reported_error);
      });
    }

    if (error.url) {
      Sentry.captureException(reported_error);
    }

    return Observable.throw(error);
  }

  private getErrorPath(errorUrl: string, errorMessage: string) {
    const url = errorUrl.indexOf('?') >= 0 ? errorUrl.split('?')[0] : errorUrl;
    return (url + '-' + errorMessage).replace(/\d+/g, 'N');
  }

  private isUnknownAppStartupApiError(error) {
    // 0: Unknown error, offen appears on sites which don't use Service Worker
    // 504: Gateway Timeout
    if (error.status !== 0 && error.status !== 504) {
      return false;
    }

    const startUpApis = [
      apiEndpoints.me,
      apiEndpoints.getGoverningAgencyByAgency,
      apiEndpoints.getAllAdvertiser,
      apiEndpoints.definitions
    ];

    switch (error.status) {
      case 0:
        const innerError = error.error;
        const target = innerError && innerError.currentTarget;

        if (target instanceof XMLHttpRequest) {
          const url = target['__zone_symbol__xhrURL'];
          const urlEl = document.createElement('a');
          urlEl.href = url;
          return startUpApis.some(api => urlEl.pathname.indexOf(api) >= 0);
        }

        return false;

      case 504:
        const errorUrlEl = document.createElement('a');
        errorUrlEl.href = error.url;
        return startUpApis.some(api => errorUrlEl.pathname.indexOf(api) >= 0);

      default:
        return false;
    }
  }
}



// WEBPACK FOOTER //
// ./src/app/core/backend-http.service.ts